import { supabase } from '@/lib/supabase'

export type PushScheduleMode = 'all_days' | 'weekdays' | 'custom' | 'off'
export type PushDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export type PushDayConfig = {
  enabled: boolean
  startMin: number
  endMin: number
}

export type UserPushPreferences = {
  userId: string
  timezone: string
  mode: PushScheduleMode
  defaultStartMin: number
  defaultEndMin: number
  days: Record<PushDayKey, PushDayConfig>
  snoozeUntil: string | null
}

const DAY_KEYS: PushDayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const DEFAULT_START_MIN = 8 * 60 // 08:00
const DEFAULT_END_MIN = 22 * 60 // 22:00

function buildDefaultDays(startMin: number, endMin: number): Record<PushDayKey, PushDayConfig> {
  return DAY_KEYS.reduce((acc, key) => {
    acc[key] = { enabled: true, startMin, endMin }
    return acc
  }, {} as Record<PushDayKey, PushDayConfig>)
}

export function createDefaultPreferences(userId: string, timezone: string): UserPushPreferences {
  return {
    userId,
    timezone,
    mode: 'all_days',
    defaultStartMin: DEFAULT_START_MIN,
    defaultEndMin: DEFAULT_END_MIN,
    days: buildDefaultDays(DEFAULT_START_MIN, DEFAULT_END_MIN),
    snoozeUntil: null,
  }
}

function normalizeCustomDays(raw: any, fallbackStart: number, fallbackEnd: number): Record<PushDayKey, PushDayConfig> {
  const base = buildDefaultDays(fallbackStart, fallbackEnd)
  if (!raw || typeof raw !== 'object') return base
  const json = raw as Record<string, any>
  for (const key of DAY_KEYS) {
    const item = json[key]
    if (!item || typeof item !== 'object') continue
    let enabled = false
    let startMin = fallbackStart
    let endMin = fallbackEnd
    try {
      enabled = Boolean(item.enabled)
    } catch {
      enabled = false
    }
    try {
      const v = Number(item.start_min ?? item.startMin)
      if (Number.isFinite(v)) startMin = v
    } catch {}
    try {
      const v = Number(item.end_min ?? item.endMin)
      if (Number.isFinite(v)) endMin = v
    } catch {}
    base[key] = {
      enabled,
      startMin,
      endMin,
    }
  }
  return base
}

export async function fetchUserPushPreferences(userId: string): Promise<UserPushPreferences | null> {
  const { data, error } = await supabase
    .from('user_push_preferences')
    .select('user_id, timezone, mode, default_start_min, default_end_min, custom_days, snooze_until')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && (error as any).code !== 'PGRST116') {
    throw error
  }

  if (!data) {
    return null
  }

  const timezone = String((data as any).timezone || 'Etc/UTC')
  const mode = ((data as any).mode || 'all_days') as PushScheduleMode
  const defaultStartMin = Number((data as any).default_start_min ?? DEFAULT_START_MIN) || DEFAULT_START_MIN
  const defaultEndMin = Number((data as any).default_end_min ?? DEFAULT_END_MIN) || DEFAULT_END_MIN
  const days = normalizeCustomDays((data as any).custom_days, defaultStartMin, defaultEndMin)

  return {
    userId,
    timezone,
    mode,
    defaultStartMin,
    defaultEndMin,
    days,
    snoozeUntil: (data as any).snooze_until || null,
  }
}

export type UpsertPushPreferencesInput = {
  userId: string
  timezone: string
  mode: PushScheduleMode
  defaultStartMin: number
  defaultEndMin: number
  days: Record<PushDayKey, PushDayConfig>
  snoozeUntil: string | null
}

export async function upsertUserPushPreferences(input: UpsertPushPreferencesInput): Promise<UserPushPreferences> {
  const payload: any = {
    user_id: input.userId,
    timezone: input.timezone,
    mode: input.mode,
    default_start_min: input.defaultStartMin,
    default_end_min: input.defaultEndMin,
    snooze_until: input.snoozeUntil,
  }

  if (input.mode === 'custom') {
    const customDays: Record<string, any> = {}
    for (const key of DAY_KEYS) {
      const cfg = input.days[key]
      customDays[key] = {
        enabled: !!cfg?.enabled,
        start_min: cfg?.startMin ?? input.defaultStartMin,
        end_min: cfg?.endMin ?? input.defaultEndMin,
      }
    }
    payload.custom_days = customDays
  } else {
    payload.custom_days = null
  }

  const { data, error } = await supabase
    .from('user_push_preferences')
    .upsert(payload, { onConflict: 'user_id' })
    .select('user_id, timezone, mode, default_start_min, default_end_min, custom_days, snooze_until')
    .maybeSingle()

  if (error) {
    throw error
  }

  const row = data as any
  const timezone = String(row.timezone || input.timezone || 'Etc/UTC')
  const mode = (row.mode || input.mode) as PushScheduleMode
  const defaultStartMin = Number(row.default_start_min ?? input.defaultStartMin ?? DEFAULT_START_MIN) || DEFAULT_START_MIN
  const defaultEndMin = Number(row.default_end_min ?? input.defaultEndMin ?? DEFAULT_END_MIN) || DEFAULT_END_MIN
  const days = normalizeCustomDays(row.custom_days, defaultStartMin, defaultEndMin)

  return {
    userId: input.userId,
    timezone,
    mode,
    defaultStartMin,
    defaultEndMin,
    days,
    snoozeUntil: row.snooze_until || null,
  }
}

